import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { GATEWAY_TYPE_PAYPAL } from '../constants/gatewayTypes';
import { toDecimals } from '../../../../util/currency';
import { PaymentContext } from '../contexts/PaymentContext';

export class PayPalCreditCard extends Component {
  constructor(props) {
    super(props);
    this.checkout = null;
    this.state = {
      orderID: '',
      zip: '',
      address: {
        name: this.props.name,
        address: this.props.defaultAddress.address,
        address2: this.props.defaultAddress.address2,
        city: this.props.defaultAddress.city,
        state: this.props.defaultAddress.state,
        country: this.props.defaultAddress.country,
        zip: this.props.defaultAddress.zip,
        phone: this.props.phone,
      },
      loading: false,
      showPaypalButtons: true,
    };
  }

  onSuccess = () => {
    this.setState({ showPaypalButtons: false, loading: false });
  };

  onError = () => {
    this.setState({ loading: false });
  };

  submitPayment = async (paymentDetails) => {
    let authorizationID = paymentDetails.purchase_units[0].payments.authorizations[0].id;
    if (this.state.loading !== true) {
      this.setState({ loading: true });
    }
    if (this.props.isFullyPaid) {
      return;
    }

    this.props.onPaymentInitiated();

    let address = paymentDetails.payer.address.address_line_1
      ? paymentDetails.payer.address
      : paymentDetails.purchase_units[0].shipping.address;
    let addressInput = {
      name: paymentDetails.payer.name.given_name + ' ' + paymentDetails.payer.name.surname,
      address: address.address_line_1,
      address2: address.address_line_2,
      city: address.admin_area_2,
      state: address.admin_area_1,
      country: address.country_code,
      zip: address.postal_code,
      phone: this.props.phone,
    };

    let payload = {
      name: this.state.address.name,
      number: '',
      expiration_month: '',
      expiration_year: '',
      security_code: '',
      ...addressInput,
      amount: this.props.amount,
      email: this.props.email,
      tos_accepted: this.props.tosAccepted,
      captureMethod: this.props.captureMethod,
      emailOptOut: this.props.emailOptOut,
      card_token: authorizationID,
      reservationToken: this.props.token,
      payment_type: GATEWAY_TYPE_PAYPAL,
      payer_id: paymentDetails.payer.payer_id,
    };

    if (this.props.createdNewAccount) {
      payload.sendAccountEmail = 1;
    }

    this.props.setDeliveryAddress({
      address: paymentDetails.purchase_units[0].shipping.address.address_line_1,
      address2: paymentDetails.purchase_units[0].shipping.address.address_line_2,
      name: paymentDetails.purchase_units[0].shipping.name.full_name,
      city: paymentDetails.purchase_units[0].shipping.address.admin_area_2,
      state: paymentDetails.purchase_units[0].shipping.address.admin_area_1,
      country: paymentDetails.purchase_units[0].shipping.address.country_code,
      zip: paymentDetails.purchase_units[0].shipping.address.postal_code,
    });

    if (!(await this.props.validateAndSaveDeliveryAddress(this.props.t))) {
      this.setState({ loading: false });
      return;
    }

    this.props.onPaymentSubmit(payload, { onSuccess: this.onSuccess, onError: this.onError });
  };

  render() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let that = this;
    const amount = toDecimals({ amount: this.props.amount, currency: this.props.currency });
    return (
      <PaymentContext.Consumer>
        {({ OptInsOuts, LoyaltyProgram, Insurance }) => (
          <section className="checkout-form__section">
            {LoyaltyProgram}
            {Insurance}
            {OptInsOuts}
            <PayPalScriptProvider
              options={{ 'client-id': this.props.gatewayKey, 'integration-date': '2022-06-14', intent: 'authorize' }}
            >
              {this.state.showPaypalButtons &&
                this.props.fundingSources.map((fundingSource) => {
                  return (
                    <PayPalButtons
                      style={{ layout: 'vertical' }}
                      disabled={!this.props.tosAccepted || this.state.loading || this.props.loading}
                      forceReRender={[amount, this.props.currency, this.props.tosAccepted]}
                      fundingSource={fundingSource}
                      createOrder={(data, actions) => {
                        return actions.order
                          .create({
                            purchase_units: [
                              {
                                amount: {
                                  currency_code: this.props.currency,
                                  value: amount,
                                },
                              },
                            ],
                            intent: 'AUTHORIZE',
                          })
                          .then((orderId) => {
                            return orderId;
                          });
                      }}
                      onApprove={function (data, actions) {
                        return actions.order.authorize().then(that.submitPayment);
                      }}
                    />
                  );
                })}
            </PayPalScriptProvider>
          </section>
        )}
      </PaymentContext.Consumer>
    );
  }
}

PayPalCreditCard.propTypes = {
  onPaymentInitiated: PropTypes.func.isRequired,
  onPaymentSuccess: PropTypes.func.isRequired,
  onPaymentFail: PropTypes.func.isRequired,
  amount: PropTypes.number.isRequired,
  email: PropTypes.string.isRequired,
  name: PropTypes.string,
  phone: PropTypes.string,
  token: PropTypes.string.isRequired,
  secret: PropTypes.string.isRequired,
  apiUrl: PropTypes.string.isRequired,
  tosAccepted: PropTypes.bool.isRequired,
  acceptedCards: PropTypes.object,
  captureMethod: PropTypes.number,
  isFullyPaid: PropTypes.bool,
  createdNewAccount: PropTypes.bool,
  emailOptOut: PropTypes.bool,
  gatewayKey: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  validateAndSaveDeliveryAddress: PropTypes.func.isRequired,
  setDeliveryAddress: PropTypes.func.isRequired,
  deliveryAddress: PropTypes.object.isRequired,
  fundingSources: PropTypes.array.isRequired,
  t: PropTypes.object.isRequired,
  defaultAddress: PropTypes.object,
};

PayPalCreditCard.defaultProps = {
  phone: '',
  name: '',
  acceptedCards: {},
  captureMethod: 1, // CAPTURE_ONLINE
  defaultAddress: {},
  fundingSources: [],
};

export default PayPalCreditCard;
